import React, { useState, useEffect } from 'react';
import { t } from '../tokens';
import FeaturesSheet from './FeaturesSheet';

interface WelcomeScreenProps {
  onNewChart: () => void;
  onOpen: () => void;
  onAccount: () => void;
  onSettings: () => void;
  onJamSession?: () => void;
}

const WelcomeScreen: React.FC<WelcomeScreenProps> = ({ onNewChart, onOpen, onAccount, onSettings, onJamSession }) => {
  const [showFeatures, setShowFeatures] = useState(false);

  useEffect(() => {
    const prev = document.body.style.backgroundColor;
    document.body.style.backgroundColor = t.bg;
    return () => { document.body.style.backgroundColor = prev; };
  }, []);

  // ─── Exact Figma tokens — node #248:58 (smartphone_landing, 402×874) ───────
  // fill_8ba6f23a=#271B13  fill_ffc01e8c=#D3CCBD  fill_985af6a3=#D4A853
  // fill_b637d039=#56463C  fill_658ab2fa=#FFFFFF (separators)
  const bg        = t.bg;
  const beige     = t.beige; // fill_ffc01e8c
  const gold      = t.gold; // fill_985af6a3
  const darkBrown = t.darkBrown; // fill_8ba6f23a
  const medBrown  = t.medBrown; // fill_b637d039

  // style_946ff0b0 — primary button text: Kumbh Sans 500, 28.8px, lh 1.25em, textCase SMALL_CAPS
  // Figma strings are already all-caps ("NEW CHART", "OPEN"…) → no transform needed
  const btnPrimary: React.CSSProperties = {
    width: '304.8px',
    height: '51.6px',
    backgroundColor: beige,
    borderRadius: '27.6px',
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'Kumbh Sans, sans-serif',
    fontWeight: 500,
    fontSize: '28.8px',
    lineHeight: '1.25em',
    color: darkBrown,
    display: 'block',
    flexShrink: 0,
  };

  // style_38400441 — secondary button text: Kumbh Sans 400, 16px, ls 0.04em, textCase SMALL_CAPS
  // Figma strings already all-caps ("ACCOUNT & SUBSCRIPTION", "SETTINGS & TOOLS")
  const btnSecondary: React.CSSProperties = {
    width: '269px',
    height: '43px',
    backgroundColor: medBrown,
    borderRadius: '23px',
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'Kumbh Sans, sans-serif',
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: '1.25em',
    letterSpacing: '0.04em',
    color: gold,
    display: 'block',
    flexShrink: 0,
  };

  // Separators #249:31 and #309:219 — strokes: fill_658ab2fa = #FFFFFF, strokeWeight 1px, width 203px
  const separator: React.CSSProperties = {
    width: '203px',
    height: '1px',
    backgroundColor: '#FFFFFF',
    flexShrink: 0,
  };

  return (
    <div
      style={{
        minHeight: '100dvh',
        width: '100%',
        backgroundColor: bg,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        overflowY: 'auto',
        paddingTop: '32px',
        paddingBottom: '32px',
      }}
    >
      {/* Figma frame #248:58 — 402×874 */}
      <div
        style={{
          position: 'relative',
          width: '402px',
          maxWidth: '100vw',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingBottom: '49px',
        }}
      >
        {/* ── Logo group #262:98 — exported as single SVG from Figma, 137×139px ── */}
        <img
          src="/figma-assets/landing-logo.svg"
          alt="Chord Notator"
          style={{ width: '146px', height: '139.55px', flexShrink: 0 }}
        />

        {/* #249:28 — "edit  /  gig  /  jam", Kumbh Sans 400, 20px, ls 0.1em,
            textCase: UPPER → text-transform: uppercase, #D4A853, y:184 (gap ≈ 12px) */}
        <p
          style={{
            marginTop: '12px',
            fontFamily: 'Kumbh Sans, sans-serif',
            fontWeight: 400,
            fontSize: '20px',
            lineHeight: '1.25em',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: gold,
            textAlign: 'center',
          }}
        >
          edit  /  gig  /  jam
        </p>

        {/* Separator 1 — #249:31, y:232, strokes #FFFFFF, 203×1px */}
        <div style={{ ...separator, marginTop: '23px' }} />

        {/* #309:218 — Kumbh Sans 400, 20px, lh 1.25em, textCase SMALL_CAPS, #D4A853, y:252
            textCase SMALL_CAPS → font-variant-caps: small-caps; but Figma source string
            is mixed-case and user expects it to render as lowercase → no transform */}
        <p
          style={{
            marginTop: '20px',
            width: '356px',
            maxWidth: '100%',
            padding: '0 23px',
            fontFamily: 'Kumbh Sans, sans-serif',
            fontWeight: 400,
            fontSize: '20px',
            lineHeight: '1.25em',
            color: gold,
            textAlign: 'center',
            whiteSpace: 'pre-line',
          }}
        >
          {'Your professional chord chart companion for all music\nCreate, edit, share and perform with professional chord charts.\nAll Device Access anytime.'}
        </p>

        {/* Separator 2 — #309:219, y:397, strokes #FFFFFF, 203×1px */}
        <div style={{ ...separator, marginTop: '20px' }} />

        {/* #248:114 — text: "NEW" (exact Figma string), style_946ff0b0, y:387 */}
        <button className="pill-btn pill-btn--1" style={{ ...btnPrimary, marginTop: '52px' }} onClick={onNewChart}>
          NEW
        </button>

        {/* #248:116 — text: "OPEN" (exact Figma string), style_946ff0b0, y:472.2 */}
        <button className="pill-btn pill-btn--2" style={{ ...btnPrimary, marginTop: '33.6px' }} onClick={onOpen}>
          OPEN
        </button>

        {/* #249:30 — text: "JAM" (exact Figma string), style_946ff0b0, y:557.4 */}
        <button className="pill-btn pill-btn--3" style={{ ...btnPrimary, marginTop: '33.6px' }} onClick={onJamSession}>
          JAM
        </button>

        {/* #248:120 — text: "ACCOUNT & SUBSCRIPTION" (exact Figma string), style_38400441, y:732 */}
        <button className="pill-btn pill-btn--4" style={{ ...btnSecondary, marginTop: '49px' }} onClick={onAccount}>
          ACCOUNT &amp; SUBSCRIPTION
        </button>

        {/* #248:122 — text: "SETTINGS & TOOLS" (exact Figma string), style_38400441, y:793 */}
        <button className="pill-btn pill-btn--5" style={{ ...btnSecondary, marginTop: '19px' }} onClick={onSettings}>
          SETTINGS &amp; TOOLS
        </button>

        {/* Features button — small, unobtrusive, below secondary buttons */}
        <button
          className="pill-btn pill-btn--5"
          onClick={() => setShowFeatures(true)}
          style={{
            marginTop: '28px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'Kumbh Sans, sans-serif',
            fontWeight: 400,
            fontSize: '13px',
            letterSpacing: '0.08em',
            color: medBrown,
            textTransform: 'uppercase' as const,
            padding: '8px 16px',
          }}
        >
          features ↓
        </button>
      </div>

      {/* Features bottom sheet */}
      {showFeatures && <FeaturesSheet onClose={() => setShowFeatures(false)} />}
    </div>
  );
};

export default WelcomeScreen;